'use client';

import React, { useState } from 'react';
import { Button, ButtonColor, Input, ReviewRating } from '@shared/ui';
import styles from './product-reviews.module.css';

interface ReviewFormModalProps {
  locale: {
    REVIEWS_FORM_TITLE?: string;
    REVIEWS_FORM_NAME_LABEL?: string;
    REVIEWS_FORM_NAME_PLACEHOLDER?: string;
    REVIEWS_FORM_RATING_LABEL?: string;
    REVIEWS_FORM_REVIEW_PLACEHOLDER?: string;
    REVIEWS_FORM_SUBMIT_BUTTON?: string;
    REVIEWS_FORM_SUBMIT_LOADING?: string;
    REVIEWS_FORM_SUCCESS_MESSAGE?: string;
    REVIEWS_FORM_ERROR_MESSAGE?: string;
    FORM_NAME_MAX_LENGTH_ERROR?: string;
    FORM_TEXT_MAX_LENGTH_ERROR?: string;
    REVIEWS_ANONYMOUS?: string;
  };
  onClose?: () => void;
  onSubmit?: (data: { name: string; rating: number; text: string }, callbacks: { onSuccess: () => void; onError: () => void }) => void;
}

export const ReviewFormModal = ({ locale, onClose, onSubmit }: ReviewFormModalProps) => {
  const [name, setName] = useState('');
  const [rating, setRating] = useState(0);
  const [text, setText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [nameError, setNameError] = useState('');
  const [textError, setTextError] = useState('');

  // Константы для максимальной длины
  const MAX_NAME_LENGTH = 100;
  const MAX_TEXT_LENGTH = 600;

  // Функции валидации
  const validateName = (value: string) => {
    // Валидация только если поле заполнено (поле опционально)
    if (value.trim().length > 0 && value.length > MAX_NAME_LENGTH) {
      setNameError(locale.FORM_NAME_MAX_LENGTH_ERROR || 'FORM_NAME_MAX_LENGTH_ERROR');
      return false;
    }
    setNameError('');
    return true;
  };

  const validateText = (value: string) => {
    // Валидация только если поле заполнено (поле опционально)
    if (value.trim().length > 0 && value.length > MAX_TEXT_LENGTH) {
      setTextError(locale.FORM_TEXT_MAX_LENGTH_ERROR || 'FORM_TEXT_MAX_LENGTH_ERROR');
      return false;
    }
    setTextError('');
    return true;
  };

  // Публичные методы для управления состоянием
  const handleSuccess = () => {
    setIsSubmitted(true);
    setIsSubmitting(false);
    
    // Закрываем модалку через 2 секунды после успешной отправки
    setTimeout(() => {
      onClose?.();
    }, 2000);
  };

  const handleError = () => {
    setIsSubmitting(false);
    // При ошибке не меняем isSubmitted, остаемся в форме
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (rating === 0) {
      return;
    }

    // Валидация длины полей
    const isNameValid = validateName(name);
    const isTextValid = validateText(text);
    
    if (!isNameValid || !isTextValid) {
      return;
    }
    
    setIsSubmitting(true);
    
    // Вызываем onSubmit с колбэками для управления состоянием
    onSubmit?.({ 
      name: name.trim() || (locale.REVIEWS_ANONYMOUS || 'Аноним'), 
      rating, 
      text: text.trim() 
    }, {
      onSuccess: handleSuccess,
      onError: handleError
    });
  };

  if (isSubmitted) {
    return (
      <div className={styles.reviewFormModal}>
        <div className={styles.reviewFormModalContent}>
          <div className={styles.reviewFormModalSuccess}>
            <h3 className={styles.reviewFormModalTitle}>
              {locale.REVIEWS_FORM_SUCCESS_MESSAGE || 'Спасибо! Ваш отзыв отправлен.'}
            </h3>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.reviewFormModal}>
      <div className={styles.reviewFormModalContent}>
        <form onSubmit={handleSubmit} className={styles.reviewFormModalForm}>
          {/* Имя автора (опциональное поле) */}
          <div className={styles.reviewFormModalField}>
            <label className={styles.reviewFormModalLabel}>
              {locale.REVIEWS_FORM_NAME_LABEL || 'Ваше имя'}
            </label>
            <Input
              placeholder={locale.REVIEWS_FORM_NAME_PLACEHOLDER || 'Введите ваше имя'}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                validateName(e.target.value);
              }}
              className={styles.reviewFormModalInput}
            />
          </div>
          {nameError && (
            <div 
              className={styles.validationError}
              style={{
                color: '#ff0000',
                fontSize: '14px',
                fontWeight: 'normal',
                textAlign: 'center',
                display: 'block',
                width: '100%'
              }}
            >
              {nameError}
            </div>
          )}

          {/* Рейтинг */}
          <div className={styles.reviewFormModalField}>
            <label className={styles.reviewFormModalLabel}>
              {locale.REVIEWS_FORM_RATING_LABEL || 'Ваша оценка'} *
            </label>
            <div className={styles.reviewFormModalRating}>
              <ReviewRating
                rate={rating}
                onChange={setRating}
                interactive
                size="large"
              />
            </div>
          </div>

          {/* Текст отзыва (опциональное поле) */}
          <div className={styles.reviewFormModalField}>
            <label className={styles.reviewFormModalLabel}>
              {locale.REVIEWS_FORM_REVIEW_PLACEHOLDER || 'Текст отзыва'}
            </label>
            <textarea
              placeholder={locale.REVIEWS_FORM_REVIEW_PLACEHOLDER || 'Напишите ваш отзыв...'}
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                validateText(e.target.value);
              }}
              className={styles.reviewFormModalTextarea}
              rows={4}
            />
          </div>
          {textError && (
            <div 
              className={styles.validationError}
              style={{
                color: '#ff0000',
                fontSize: '14px',
                fontWeight: 'normal',
                textAlign: 'center',
                display: 'block',
                width: '100%'
              }}
            >
              {textError}
            </div>
          )}

          {/* Кнопка отправить */}
          <div className={styles.reviewFormModalActions}>
            <Button
              type="submit"
              color={ButtonColor.GREEN}
              disabled={rating === 0 || isSubmitting}
              loading={isSubmitting}
              className={styles.reviewFormModalSubmitBtn}
            >
              {isSubmitting 
                ? (locale.REVIEWS_FORM_SUBMIT_LOADING || 'Отправка...')
                : (locale.REVIEWS_FORM_SUBMIT_BUTTON || 'Отправить отзыв')
              }
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}; 